import { IEmployeeProfile } from 'src/Common/Interfaces/Profile/IEmployeeProfile';
import ApiBaseUrl from '../../ApiBaseURL';
import decodeToken from './DecodeToken';
import { getPostLoginHomePath } from 'src/Common/navigation/getPostLoginHomePath';
// Helper function to handle API responses
const handleResponse = async (response: Response) => {
   
    if (!response.ok) {
        const errorText = await response.text(); // Get the response as text
        console.error(`Error response from API: ${errorText}`); // Log the raw response for debugging
        
        // Try to parse JSON error response
        let errorMessage = errorText || response.statusText;
        try {
            const errorJson = JSON.parse(errorText);
            // Backend returns structured errors with 'error', 'message', and 'details'
            if (errorJson.message) {
                errorMessage = errorJson.message;
                if (errorJson.details) {
                    errorMessage += ` (${errorJson.details})`;
                }
            } else if (errorJson.error) {
                errorMessage = errorJson.error;
                if (errorJson.message) {
                    errorMessage += `: ${errorJson.message}`;
                }
            }
        } catch {
            // If not JSON, use the text as is
            errorMessage = errorText || response.statusText;
        }
        
        throw new Error(`Error ${response.status}: ${errorMessage}`);       
    }
    
    // Handle 204 No Content responses (PUT/DELETE operations)
    if (response.status === 204 || response.status === 205) {
        return null;
    }
    
    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        return null;
    }
    
    return response.json(); // If response is okay, parse as JSON
};

function persistPermissionGroupsFromLogin(data: any) {
    const pg = data?.permissionGroups ?? data?.PermissionGroups;
    if (pg && Array.isArray(pg)) {
        localStorage.setItem('permissionGroups', JSON.stringify(pg));
    } else {
        localStorage.removeItem('permissionGroups');
    }
}

export function persistRestrictToBillingFromLogin(data: any) {
    const v = data?.restrictToBilling ?? data?.RestrictToBilling;
    localStorage.setItem('restrictToBilling', v === true || v === 'true' ? 'true' : 'false');
}

function persistRolesFromLogin(data: any) {
    const roles = data?.roles ?? data?.Roles;
    if (Array.isArray(roles)) {
        localStorage.setItem('userRoles', roles.join(','));
    } else if (roles != null && roles !== '') {
        localStorage.setItem('userRoles', String(roles));
    } else {
        localStorage.setItem('userRoles', '');
    }
}

// Authoritative company country from the login response (sourced from CompanyInformation, e.g. the
// dial code "+92" for Pakistan, or "" when unknown). Only writes when the response actually carries
// a non-empty value, so it never blanks an already-set companyCountry. Call this BEFORE
// fetchCompanySettings so the definitive value wins and the settings fetch merely fills gaps.
function persistCompanyCountryFromLogin(data: any) {
    const cc = data?.companyCountry ?? data?.CompanyCountry;
    if (typeof cc === 'string' && cc.trim() !== '') {
        localStorage.setItem('companyCountry', String(cc));
    }
}

const apiFetch = async (endpoint: string, options: RequestInit = {}) => {


    const token = sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');
    
    // If the body is FormData, do not include Content-Type header
    const isFormData = options.body instanceof FormData;
    
    const defaultHeaders = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  
    // Merge with any headers from options (but avoid overriding for FormData)
    const headers = {
      ...defaultHeaders,
      ...options.headers,
    };
  
    const isAbsoluteUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
    const url = isAbsoluteUrl ? endpoint : `${ApiBaseUrl.apiBaseUrl}/${endpoint}`;
  
    const response = await fetch(url, { ...options, headers });
    return handleResponse(response);
  };
  

  // Add this new function for handling blob responses
export const apiFetchBlob = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('authToken');
  const isFormData = options.body instanceof FormData;
  
  const defaultHeaders = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  const headers = {
    ...defaultHeaders,
    ...options.headers,
  };

  const isAbsoluteUrl = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  const url = isAbsoluteUrl ? endpoint : `${ApiBaseUrl.apiBaseUrl}/${endpoint}`;

  const response = await fetch(url, { ...options, headers });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error response from API: ${errorText}`);
    throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
  }
  
  return response.blob();
};

// Fetch ZATCA integration status and persist to localStorage
export const fetchZatcaStatus = async () => {
    try {
        const data = await apiFetch('CompanyZatcaIntegrations', { method: 'GET' });
        const enabled = data?.isZatcaIntegrationEnabled ?? data?.IsZatcaIntegrationEnabled ?? false;
        localStorage.setItem('zatcaEnabled', enabled ? 'true' : 'false');
    } catch {
        // Non-blocking — ZATCA may not be configured yet
        localStorage.setItem('zatcaEnabled', 'false');
    }
};

// Fetch company settings
export const fetchCompanySettings = async () => {
    try {
        const settings = await apiFetch('CompanySettings', {
            method: 'GET',
        });
        if (settings && settings.settings) {
            sessionStorage.setItem('companySettings', JSON.stringify(settings.settings));
            // Let ThemeProvider re-resolve the company AppTheme (Full Modern) without a reload
            window.dispatchEvent(new Event('appThemeChanged'));
            console.log("Company settings saved:", settings.settings);
            // Fallback fill of companyCountry from CompanySettings. This runs AFTER the authoritative
            // login-response value has been set, so only write when settings actually carries a value —
            // never blank out (or clobber with an empty) an already-set companyCountry.
            const settingsCountry = settings.settings.countryCode;
            if (typeof settingsCountry === 'string' && settingsCountry.trim() !== '') {
                localStorage.setItem('companyCountry', settingsCountry);
            }
        }
        return settings;
    } catch (error) {
        console.error('Failed to fetch company settings:', error);
        // Don't throw - allow login to proceed with default settings
        return null;
    }
};

// Login function
export const login = async (username: string, password: string) => {
    const payload = {
        userName: username,
        password: password,
    };

    try {
        const data = await apiFetch('Authenticate/login', {
            method: 'POST',
            body: JSON.stringify(payload), // Convert payload to JSON
        });

        // Save token if successful
        if (data.token) {

            localStorage.setItem('authToken', data.token);
            persistRolesFromLogin(data);
            persistPermissionGroupsFromLogin(data);
            persistRestrictToBillingFromLogin(data);
            persistCompanyCountryFromLogin(data);
            // Save store type if provided
            if ((data as any).StoreType || (data as any).storeType) {
                localStorage.setItem('storeType', (data as any).StoreType || (data as any).storeType);
            }

            console.log("Token saved in localStorage:", data.token);

              // Decode the token to extract employee information
              const employeeProfile = decodeToken(data.token);
            
              // Save employee information to localStorage or context
              localStorage.setItem('employeeProfile', JSON.stringify(employeeProfile));
              console.log("Employee profile saved:", employeeProfile);

              // Fetch company settings and ZATCA status after successful login
              await fetchCompanySettings();
              await fetchZatcaStatus();
              
        } else {
            console.warn('Login succeeded but no accessToken received:', data);
        }

        return data;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
};

// Developer login — opens the store in a new popup window.
// The popup itself calls Authenticate/developer-login using the developer's localStorage token
// (same-origin windows share localStorage). The result goes into the popup's sessionStorage
// (tab-specific), so the developer's main-window session is never touched.
export const developerLoginNewTab = (companyId: number, targetUserId?: string) => {
    // The user id rides in the URL so the popup can call developer-login itself;
    // it is validated server-side against the company's own active users.
    const target = targetUserId ? `&devUser=${encodeURIComponent(targetUserId)}` : '';
    window.open(
        `${window.location.origin}/?devLaunch=${companyId}${target}`,
        `cloudpos_store_${companyId}`,
        'width=1440,height=900,top=50,left=50,menubar=no,toolbar=no,location=yes,scrollbars=yes,resizable=yes'
    );
};

// Developer login function - allows developers to login as any user from a company
export const developerLogin = async (companyId: number, targetUserId?: string) => {
    const payload: any = {
        companyId: companyId,
    };
    // Omitted entirely when not chosen, so the API keeps its historic default
    // (StoreOwner, else earliest active user).
    if (targetUserId) payload.targetUserId = targetUserId;

    try {
        const data = await apiFetch('Authenticate/developer-login', {
            method: 'POST',
            body: JSON.stringify(payload),
        });

        // Save token if successful
        if (data.token) {
            localStorage.setItem('authToken', data.token);
            
            persistRolesFromLogin(data);
            persistPermissionGroupsFromLogin(data);
            persistRestrictToBillingFromLogin(data);
            persistCompanyCountryFromLogin(data);

            // Save store type if provided
            const storeType = (data as any).StoreType || (data as any).storeType;
            if (storeType) {
                localStorage.setItem('storeType', storeType);
                // Trigger store type change event for sidebar context
                window.dispatchEvent(new CustomEvent('storeTypeChanged', { 
                    detail: { storeType } 
                }));
            }

            console.log("Developer login token saved in localStorage:", data.token);

            // Decode the token to extract employee information
            const employeeProfile = decodeToken(data.token);
            
            // Save employee information to localStorage
            localStorage.setItem('employeeProfile', JSON.stringify(employeeProfile));
            console.log("Employee profile saved:", employeeProfile);

            // Fetch company settings and ZATCA status after successful login
            await fetchCompanySettings();
            await fetchZatcaStatus();
            
            // Force a full page reload to ensure all contexts and menus are refreshed
            // This ensures the sidebar and menu items are properly loaded with the new user context
            // Use a small delay to ensure all localStorage operations complete
            setTimeout(() => {
                window.location.href = getPostLoginHomePath();
            }, 200);
        } else {
            console.warn('Developer login succeeded but no token received:', data);
        }

        return data;
    } catch (error) {
        console.error('Developer login failed:', error);
        throw error;
    }
};

export const  loginwithgoogle = async (googleId: string, email: string) => {

    const payload = {
        googleId:googleId,
        email: email,
    };

    try {
        const data = await apiFetch('Authenticate/google-login', {
            method: 'POST',
            body: JSON.stringify(payload), // Convert payload to JSON
        });

        // Save token if successful
        if (data.token) {


            localStorage.setItem('authToken', data.token);
            persistRolesFromLogin(data);
            persistPermissionGroupsFromLogin(data);
            persistRestrictToBillingFromLogin(data);
            persistCompanyCountryFromLogin(data);

            console.log("Token saved in localStorage:", data.token);

        
              // Decode the token to extract employee information
              const employeeProfile = decodeToken(data.token);
            
              // Save employee information to localStorage or context
              localStorage.setItem('employeeProfile', JSON.stringify(employeeProfile));
              console.log("Employee profile saved:", employeeProfile);

              // Fetch company settings and ZATCA status after successful login
              await fetchCompanySettings();
              await fetchZatcaStatus();
              
        } else {
            console.warn('Login succeeded but no accessToken received:', data);
        }

        return data;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
};
export const getProfileInfo = async (employeeCode: string) => {
    try {
        const response = await apiFetch(`Account/GetProfileInfo?EmployeeCode=${employeeCode}`, {
            method: 'GET',
        });
        return response as IEmployeeProfile[]; // Return as an array of IEmployeeProfile
    } catch (error) {
        console.error('Failed to fetch profile info:', error);
        throw error;
    }
};



// Forgot Password
export const forgotPasswordRequest = async (userName: string) => {
    try {
        const response = await apiFetch(`/ForgotPassword/forgotRequest?UserName=${userName}`, {
            method: 'GET',
        });
        return response;
    } catch (error) {
        console.error('Failed to send forgot password request:', error);
        throw error;
    }
};

// Change Password
export const changePassword = async (
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
) => {
    const payload = {
        OldPassword: oldPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword,
    };

    try {
        const response = await apiFetch('/Account/ChangePassword', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        return response;
    } catch (error) {
        console.error('Failed to change password:', error);
        throw error;
    }
};

// Forgot Password API
export const forgotPassword = async (email: string) => {
    try {
        const response = await fetch(`${ApiBaseUrl.apiBaseUrl}/Authenticate/ForgotPassword`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ Email: email }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(`Error sending forgot password email: ${response.statusText}`);
        } else {
            console.log('Forgot password email sent successfully');
            return true;
        }
    } catch (error) {
        console.error('Error during forgot password:', error);
        throw error;
    }
};

// Reset Password API
export const resetPassword = async (model: { Email: string; Token: string; NewPassword: string }) => {
    try {
        const response = await fetch(`${ApiBaseUrl.apiBaseUrl}/Authenticate/ResetPassword`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(model),
        });

        if (!response.ok) {
            const errorText = await response.text();
            const errorMessage = `Error ${response.status}: ${errorText || response.statusText}`;
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(errorMessage);
        } else {
            console.log('Password reset successfully');
            return true;
        }
    } catch (error) {
        console.error('Error during reset password:', error);
        throw error;
    }
};


export default apiFetch;
